import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle, Star, Play, Award, Users, Clock } from "lucide-react";

const Hero = () => {
  const handleScrollToEstimate = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Add small delay for mobile touch events
    setTimeout(() => {
      const element = document.querySelector('#estimate');
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest' 
        });
      }
    }, 100);
  };

  const handleScrollToGallery = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Add small delay for mobile touch events
    setTimeout(() => {
      const element = document.querySelector('#gallery');
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest' 
        });
      }
    }, 100);
  };

  return (
    <section id="home" className="pt-24 pb-6 flex items-center bg-gradient-subtle relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-accent rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-gold rounded-full blur-3xl"></div>
      </div>

      {/* Promotional Banner */}
      <div className="absolute top-24 left-0 right-0 bg-gradient-gold text-white py-4 z-10 shadow-medium">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center justify-center gap-6 text-center">
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold">30% OFF</span>
              <span className="text-sm opacity-90">All Construction Services</span>
            </div>
            <div className="hidden lg:block w-px h-6 bg-white/30"></div>
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold">3-Year Warranty</span>
              <span className="text-sm opacity-90">On All Painting Work</span>
            </div>
            <div className="hidden lg:block w-px h-6 bg-white/30"></div>
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold">Free Estimates</span>
              <span className="text-sm opacity-90">24/48 Hour Response</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-8 pt-44 relative z-20">
        <div className="grid xl:grid-cols-2 gap-6 items-center">
          {/* Content */}
          <div className="space-y-4 animate-fade-in">
            <div className="space-y-4">
              <div className="flex flex-wrap gap-3">
                <div className="inline-flex items-center space-x-2 bg-green-light px-4 py-2 rounded-full shadow-soft">
                  <CheckCircle className="w-4 h-4 text-green-accent" />
                  <span className="text-sm font-semibold text-green-accent">Licensed & Insured</span>
                </div>
                <div className="inline-flex items-center space-x-2 bg-gold-light px-4 py-2 rounded-full shadow-soft">
                  <Award className="w-4 h-4 text-gold-primary" />
                  <span className="text-sm font-semibold text-gold-primary">15+ Years Experience</span>
                </div>
              </div>
              
              <h1 className="text-3xl lg:text-5xl xl:text-6xl font-bold text-primary leading-tight">
                Building Your
                <span className="block bg-gradient-accent bg-clip-text text-transparent">Happy Mind</span>
              </h1>
              
              <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed">
                CHM Construction delivers exceptional renovations and construction services in Winnipeg where 
                <strong className="text-primary"> Customers Happy Mind</strong> is our promise. 
                We transform your vision into reality with stress-free, professional service.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                variant="hero" 
                size="lg" 
                className="group text-lg px-8 py-4 h-auto shadow-strong hover:shadow-medium"
                onClick={handleScrollToEstimate}
              >
                Get Free Estimate
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                className="group text-lg px-8 py-4 h-auto border-2 hover:bg-muted"
                onClick={handleScrollToGallery}
              >
                <Play className="w-5 h-5 mr-2" />
                View Our Work
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4 border-t border-border">
              <div className="flex items-center space-x-3 p-3 bg-card rounded-xl shadow-soft">
                <div className="flex items-center space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <div>
                  <div className="font-bold text-lg text-primary">4.9/5</div>
                  <div className="text-sm text-muted-foreground">Google Rating</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-3 bg-card rounded-xl shadow-soft">
                <Users className="w-8 h-8 text-green-accent" />
                <div>
                  <div className="font-bold text-lg text-primary">500+</div>
                  <div className="text-sm text-muted-foreground">Happy Customers</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-3 bg-card rounded-xl shadow-soft">
                <Clock className="w-8 h-8 text-gold-primary" />
                <div>
                  <div className="font-bold text-lg text-primary">15+</div>
                  <div className="text-sm text-muted-foreground">Years Experience</div>
                </div>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="relative animate-scale-in">
            <div className="relative rounded-3xl overflow-hidden shadow-strong">
              <img
                src="/lovable-uploads/fea6bb19-796f-4b2d-8258-a90434511cdc.png"
                alt="CHM Construction professional exterior painting and renovation services in Winnipeg"
                className="w-full h-[400px] object-cover"
                width="628"
                height="400"
                loading="eager"
                fetchPriority="high"
                decoding="sync"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-primary/30 to-transparent"></div>
              
              {/* Experience Badge */}
              <div className="absolute top-6 right-6 bg-gradient-gold text-white px-4 py-2 rounded-full shadow-medium">
                <span className="font-bold text-sm">15+ Years Excellence</span>
              </div>
            </div>
            
            {/* Floating Quality Badge */}
            <div className="absolute -bottom-8 -left-8 bg-card p-6 rounded-2xl shadow-strong border-2 border-gold-light">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-gradient-accent rounded-2xl flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <div>
                  <p className="font-bold text-lg text-primary">Quality Guaranteed</p>
                  <p className="text-sm text-muted-foreground">Licensed, Insured & Bonded</p>
                </div>
              </div>
            </div>

            {/* Floating Warranty Badge */}
            <div className="absolute -top-6 -right-6 bg-navy-primary text-white p-4 rounded-2xl shadow-strong">
              <div className="text-center">
                <div className="font-bold text-2xl">3-Year</div>
                <div className="text-sm opacity-90">Warranty</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;